import { useMutation } from "@apollo/client"
import FullscreenConfirm from "@pathwright/ui/src/components/confirm/FullscreenConfirm"
import DropdownItem from "@pathwright/ui/src/components/dropdown/DropdownItem"
import DropdownMenu from "@pathwright/ui/src/components/dropdown/DropdownMenu"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import PropTypes from "prop-types"
import { RegistrationsDocument } from "../../api/generated"
import useCohortPermissions from "../../cohort/useCohortPermissions"
import { getCohortSectionUrl } from "../../utils/urls"
import DELETE_REGISTRATION_MUTATION from "../graphql/delete-registration-mutation"
import { useRegistrationListContext } from "./RegistrationListContainer"

const ConfirmRemoval = ({ registration, ...rest }) => {
  const { t } = useTranslate()
  const {
    context: { cohortId },
    roleFilter,
    orderBy
  } = useRegistrationListContext()

  const [mutation] = useMutation(DELETE_REGISTRATION_MUTATION, {
    variables: {
      group_id: cohortId,
      registration_id: registration.id
    },
    update: (cache) => {
      const query = RegistrationsDocument
      const variables = {
        groupId: cohortId,
        roleFilter,
        orderBy
      }

      const data = cache.readQuery({ query, variables })
      const nextData = {
        group: {
          ...data.group,
          registrations: {
            ...data.group.registrations,
            edges: data.group.registrations.edges.filter(
              (edge) => edge.node.id !== registration.id
            )
          }
        }
      }
      cache.writeQuery({ query, variables, data: nextData })
    }
  })

  return (
    <FullscreenConfirm
      heading={t("Are you sure you want to remove {{ user }}?", {
        user: `${registration.user.first_name} ${registration.user.last_name}`
      })}
      body={t("This will remove all of their progress and access.")}
      confirmPrompt={t("Yes, Remove")}
      cancelPrompt={t("Cancel")}
      onConfirm={() => mutation()}
      stack
      {...rest}
    />
  )
}

const RegistrationListItemControls = ({ registration, showEmail }) => {
  const { t } = useTranslate()
  const {
    context: { cohortId }
  } = useRegistrationListContext()
  const cohortPerms = useCohortPermissions({ cohortId })

  if (!showEmail && !cohortPerms.hasModeratorLevelAccess) {
    return null
  }

  return (
    <ConfirmRemoval registration={registration}>
      {({ confirm, confirming }) => (
        <DropdownMenu>
          {showEmail && (
            <DropdownItem
              icon="envelope"
              label={registration.user.email}
              href={`mailto:${registration.user.email}`}
              target="_blank"
              closeOnClick={true}
            />
          )}
          {cohortPerms.hasModeratorLevelAccess && (
            <DropdownItem
              icon="gear"
              label={t("Configure registration")}
              to={getCohortSectionUrl(
                `/member/registration/${registration.id}/`
              )}
              closeOnClick={true}
            />
          )}
          {cohortPerms.hasModeratorLevelAccess && (
            <DropdownItem
              icon="slash"
              label={t("Remove from cohort")}
              onClick={confirm}
              preventHide={confirming}
            />
          )}
        </DropdownMenu>
      )}
    </ConfirmRemoval>
  )
}

RegistrationListItemControls.displayName = "RegistrationListItemControls"

RegistrationListItemControls.propTypes = {
  showEmail: PropTypes.bool
}

RegistrationListItemControls.defaultProps = {
  showEmail: true
}

export default RegistrationListItemControls
